import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { useMembers } from "@/hooks/use-members";
import { useOrgContext } from "@/hooks/use-org-context";
import { useEntityTypes, useWorkspaceRoot } from "@/lib/api/entities";
import { onboardingSteps } from "@/lib/onboarding";
import { cn } from "@/lib/utils";

export function OnboardingChecklist({ owner }: { owner: string }) {
	const { orgContext } = useOrgContext();
	const { org, isMember } = useCurrentOrg(owner);
	const { isAdmin, isLoading: membersLoading } = useMembers(
		isMember ? org?.id : undefined,
	);
	const canCreateEntity = isMember && isAdmin;
	// Share the existing workspace and sidebar queries. An external agent may
	// finish setup while this page is open, so refresh incomplete inventory.
	const workspace = useQuery({
		...useWorkspaceRoot.queryOptions(owner, { slug: owner }),
		refetchOnWindowFocus: "always",
		refetchInterval: (query) => {
			const counts = query.state.data?.counts;
			return counts &&
				(counts.agents + counts.clients === 0 ||
					counts.connections === 0 ||
					counts.automations === 0)
				? 15_000
				: false;
		},
	});
	const entityTypes = useQuery({
		...useEntityTypes.queryOptions(orgContext),
		refetchOnWindowFocus: "always",
		refetchInterval: (query) => {
			const counts = workspace.data?.counts;
			return counts &&
				query.state.data &&
				!onboardingSteps(
					owner,
					counts,
					query.state.data,
					canCreateEntity,
				).every((step) => step.complete)
				? 15_000
				: false;
		},
	});
	// Unknown data is not an empty workspace. Keep the sidebar quiet until
	// both existing queries have resolved, including after switching workspaces.
	if (
		(isMember && membersLoading) ||
		!workspace.data?.counts ||
		!entityTypes.data ||
		workspace.isError ||
		entityTypes.isError
	)
		return null;

	const steps = onboardingSteps(
		owner,
		workspace.data.counts,
		entityTypes.data,
		canCreateEntity,
	);
	const remaining = steps.filter((step) => !step.complete);
	if (remaining.length === 0) return null;

	return (
		<nav
			aria-label="Get started"
			className="shrink-0 border-t border-sidebar-border px-2 py-3"
		>
			<div className="flex items-center justify-between px-3 pb-2">
				<span className="text-sm font-medium">Get started</span>
				<span className="text-xs tabular-nums text-muted-foreground">
					{steps.length - remaining.length} of {steps.length}
				</span>
			</div>
			<ol className="space-y-0.5">
				{remaining.map((step, index) => (
					<li key={step.number} value={step.number}>
						<Link
							{...step.link}
							aria-current={index === 0 ? "step" : undefined}
							className={cn(
								"group flex min-h-9 items-center gap-2.5 rounded-md px-3 text-[13px] transition-colors hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
								index === 0
									? "font-medium text-sidebar-foreground"
									: "text-muted-foreground",
							)}
						>
							<span
								aria-hidden="true"
								className={cn(
									"flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] tabular-nums",
									index === 0
										? "border-primary bg-primary text-primary-foreground"
										: "border-sidebar-border",
								)}
							>
								{step.number}
							</span>
							<span className="min-w-0 flex-1">{step.label}</span>
							<ChevronRight
								aria-hidden="true"
								className="h-3.5 w-3.5 shrink-0"
							/>
						</Link>
					</li>
				))}
			</ol>
		</nav>
	);
}
